import { useEffect } from 'react';
import PropTypes from 'prop-types';

/**
 * Custom hook for global keyboard shortcuts
 *
 * Registers keyboard shortcuts for:
 * - Cmd+K / Ctrl+K: Open Global Target Finder
 * - Ctrl+Shift+W: Toggle Targeting Workspace
 *
 * Prevents default browser behavior for these shortcuts
 * (e.g., Cmd+K normally opens Chrome's search bar)
 *
 * @param {Object} options - Callback functions for shortcuts
 * @param {Function} options.onOpenTargetFinder - Called when Cmd+K / Ctrl+K pressed
 * @param {Function} options.onOpenWorkspace - Called when Ctrl+Shift+W pressed
 */
export function useGlobalShortcuts({ onOpenTargetFinder, onOpenWorkspace }) {
  useEffect(() => {
    /**
     * Handle keyboard shortcuts
     */
    const handleKeyDown = (e) => {
      // Cmd+K / Ctrl+K: Open Target Finder
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        e.stopPropagation();
        if (onOpenTargetFinder) {
          onOpenTargetFinder();
        }
        return;
      }

      // Ctrl+Shift+W: Toggle Workspace
      if (e.ctrlKey && e.shiftKey && e.key === 'W') {
        e.preventDefault();
        e.stopPropagation();
        if (onOpenWorkspace) {
          onOpenWorkspace();
        }
        return;
      }
    };

    // Register global event listener
    window.addEventListener('keydown', handleKeyDown);

    // Cleanup on unmount
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [onOpenTargetFinder, onOpenWorkspace]);
}

/**
 * PropTypes for hook options
 */
useGlobalShortcuts.propTypes = {
  onOpenTargetFinder: PropTypes.func,
  onOpenWorkspace: PropTypes.func,
};

export default useGlobalShortcuts;
